import { formatPrice } from '@rcg/pricing-format';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { getOccupancyLabel } from 'lib/occupancy/getOccupancyLabel';
import { getCurrency } from 'lib/utilities/Price';
import { useCallback, useContext, useMemo } from 'react';
import {
	CruiseCardOccupancyLabel,
	CruiseCardPriceBase,
	CruiseCardPriceContainer,
	CruiseCardPriceCurrency,
	CruiseCardPriceLabel,
	CruiseCardPriceTag,
	CruiseCardPriceValue,
} from './CruiseCardPrice.styles';
import { CruiseCardPriceProps } from './CruiseCardPrice.types';

function CruiseCardPrice({ sailing, packageId }: CruiseCardPriceProps) {
	const { globalization, locale, searchApplied }: any = useContext(CruiseSearchContext);
	const { itineraryCardsDictionary, pricingPanelDictionary, generalDictionary } = globalization?.dictionaries || {};
	const { labelAvgPerPerson, labelAvgPerPersonFor } = itineraryCardsDictionary;
	const { labelComplimentary } = pricingPanelDictionary;
	const { labelTravelersFilterAdult, labelTravelersFilterAdults, labelTravelersFilterChild, labelTravelersFilterKids } = generalDictionary;
	const currency = getCurrency(locale.currency);

	const occupancyInfo = useMemo(() => {
		const adultsCount = searchApplied?.adults?.toString();
		const childrenCount = searchApplied?.children?.toString();
		const hasAdults = searchApplied?.adults && adultsCount !== '0';
		const hasChildren = searchApplied?.children && childrenCount !== '0';
		const hasNoG = hasAdults || hasChildren;
		const occupancyLabel = getOccupancyLabel({
			adults: searchApplied?.adults?.toString(),
			children: searchApplied?.children?.toString(),
			adultLabel: labelTravelersFilterAdult,
			adultsLabel: labelTravelersFilterAdults,
			childLabel: labelTravelersFilterChild,
			childrenLabel: labelTravelersFilterKids,
		});
		const avgLabel = hasNoG ? labelAvgPerPersonFor : labelAvgPerPerson;

		return {
			hasAdults,
			hasChildren,
			occupancyLabel: hasNoG ? `${occupancyLabel}*` : '',
			avgLabel,
		};
	}, [searchApplied, labelTravelersFilterAdult, labelTravelersFilterAdults, labelTravelersFilterChild, labelTravelersFilterKids]);

	const getPriceTag = useCallback(() => {
		if (sailing.lowestPrice.netPrice === 0) {
			return <CruiseCardPriceValue className="complimentary">{labelComplimentary}</CruiseCardPriceValue>;
		}

		return (
			<>
				<CruiseCardPriceCurrency className={currency.symbolOnLeft ? 'left' : 'right'}>{currency.symbol}</CruiseCardPriceCurrency>
				<CruiseCardPriceValue>{formatPrice(sailing.lowestPrice.netPrice, locale.currency)}</CruiseCardPriceValue>
			</>
		);
	}, [sailing?.lowestPrice?.netPrice]);

	return (
		<CruiseCardPriceBase>
			<CruiseCardPriceContainer>
				<CruiseCardPriceLabel data-testid={`cruise-price-avg-label-${packageId}`}>{occupancyInfo.avgLabel}</CruiseCardPriceLabel>
				<CruiseCardOccupancyLabel>
					<>{occupancyInfo.occupancyLabel}</>
				</CruiseCardOccupancyLabel>
			</CruiseCardPriceContainer>
			<CruiseCardPriceTag data-testid={`cruise-price-label-${packageId}`}>{getPriceTag()}</CruiseCardPriceTag>
		</CruiseCardPriceBase>
	);
}

export default CruiseCardPrice;
